import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Steps,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Table,
  Divider,
  Alert,
  Checkbox,
  message,
  Image,
  Result,
  Modal,
  Radio,
  Tag,
  Grid
} from 'antd';
import {
  ShoppingCartOutlined,
  UserOutlined,
  CheckCircleOutlined,
  LeftOutlined,
  CreditCardOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { walletService } from '../services/walletService';
import { Link, useNavigate } from 'react-router-dom';
import { useCartStore } from '../stores/cartStore';
import { useAuthStore } from '../stores/authStore';
import { QuotationService, CheckoutRequest, CustomerInfo } from '../services/quotationService';
import { promoCodeService, type PromoPricingResult } from '../services/promoCodeService';
import { ProductService } from '../services/productService';
import { PaymentForm } from '../components/payment';
import type { RazorpayCheckoutSuccess } from '../config/razorpay';
import { isRazorpayConfigured } from '../config/razorpay';
import {
  loadCheckoutDraft,
  saveCheckoutDraft,
  clearCheckoutDraft,
} from '../utils/checkoutDraftStorage';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

interface CheckoutFormData {
  traderName?: string;
  address1: string;
  address2: string;
  address3: string;
  tel: string;
  email: string;
}

type CheckoutMode = 'payment' | 'wallet';
type CheckoutDraft = {
  formData: Partial<CheckoutFormData>;
  reviewCustomerInfo: CheckoutFormData | null;
  currentStep: number;
  checkoutMode: CheckoutMode;
};

const toNumber = (value: unknown): number => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatCurrency = (value: unknown): string => `$${toNumber(value).toFixed(2)}`;

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { items, summary, fetchCart, clearCart } = useCartStore();

  const [form] = Form.useForm<CheckoutFormData>();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [reviewCustomerInfo, setReviewCustomerInfo] = useState<CheckoutFormData | null>(null);
  const [terms, setTerms] = useState<string[]>([]);
  const [quotationRefNo, setQuotationRefNo] = useState<string>('');
  const [termsVisible, setTermsVisible] = useState(false);
  // T&C agreement lives on the Review Order step (issue #62) so the customer
  // confirms the terms right before placing/paying, not on the info step.
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Default to card payment; users with wallet balance can switch to wallet.
  const [checkoutMode, setCheckoutMode] = useState<CheckoutMode>(isRazorpayConfigured() ? 'payment' : 'wallet');
  const [paymentId, setPaymentId] = useState<string | null>(null);
  // Set when Razorpay captured a payment but we could not turn it into an order.
  // The money is gone, so the customer must NOT be invited to pay again.
  const [orphanedPayment, setOrphanedPayment] = useState<string | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  // False until fetchCart() has resolved once, so the empty-cart redirect can't
  // fire against the initial empty state.
  const [cartLoaded, setCartLoaded] = useState(false);
  const hasHydratedDraft = useRef(false);

  const paymentAvailable = isRazorpayConfigured();
  const screens = Grid.useBreakpoint();

  // Promo code state. `appliedPromo` holds the server-validated result; the
  // displayed totals and the checkout payload are both derived from it, so the
  // discount the customer sees is exactly what the server will recompute.
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<PromoPricingResult | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  const baseSubtotal = toNumber(summary?.subtotal ?? summary?.calculation?.totalD);
  const baseDelivery = toNumber(summary?.calculation?.deliveryAmtD);
  const baseGst = toNumber(summary?.calculation?.gstAmtD);
  const baseGrandTotal = toNumber(summary?.calculation?.gTotalD ?? summary?.calculation?.totalD ?? summary?.subtotal);
  const gstFlag = summary?.calculation?.gstStatusStr || '';
  const gstPercent = toNumber(summary?.calculation?.gstPercent);

  // Apply the promo discount pre-GST, mirroring the backend recompute exactly so
  // the grand total we send matches what the server independently derives.
  // GST here is additive; the summary's `gstFlag` actually carries GSTStatus
  // ('STD'), not the '+' flag, so detect "GST applies" from baseGst > 0 (the
  // undiscounted cart already carried a GST amount) and recompute on the
  // discounted base at the same percent.
  const discountAmt = appliedPromo?.valid ? Math.min(round2(appliedPromo.discountAmt), baseSubtotal) : 0;
  const discountedSub = round2(baseSubtotal - discountAmt);
  const gstApplies = baseGst > 0;
  const gstAmount = discountAmt > 0
    ? (gstApplies && gstPercent > 0 ? round2((discountedSub + baseDelivery) * gstPercent / 100) : 0)
    : baseGst;
  const grandTotal = discountAmt > 0 ? round2(discountedSub + baseDelivery + gstAmount) : baseGrandTotal;

  const checkoutSummary = {
    subtotal: baseSubtotal,
    deliveryAmount: baseDelivery,
    gstAmount,
    grandTotal,
    gstFlag,
    gstPercent,
    discountAmt,
  };

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) { setPromoError('Please enter a promo code'); return; }
    setPromoLoading(true);
    setPromoError(null);
    try {
      const result = await promoCodeService.validate(
        code,
        items.map(it => ({ SupersedeNo: it.SupersedeNo, Qty: it.Qty, Price: it.Price }))
      );
      if (result.valid) {
        setAppliedPromo(result);
        message.success('Promo code applied');
      } else {
        setAppliedPromo(null);
        setPromoError(result.message || 'This promo code cannot be applied');
      }
    } catch (err: any) {
      setAppliedPromo(null);
      setPromoError(err?.response?.data?.message || 'Failed to validate promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError(null);
  };

  // A changing cart invalidates any applied promo (eligible items/subtotal may
  // have shifted); clear it so the customer re-applies against the new cart.
  useEffect(() => {
    setAppliedPromo(null);
    setPromoError(null);
  }, [items]);

  const persistCheckoutDraft = (formDataOverride?: Partial<CheckoutFormData>) => {
    if (!hasHydratedDraft.current) {
      return;
    }

    const formData = formDataOverride ?? form.getFieldsValue(true);
    const draft: CheckoutDraft = {
      formData,
      reviewCustomerInfo,
      currentStep,
      checkoutMode
    };

    saveCheckoutDraft(draft);
  };

  useEffect(() => {
    const savedDraft = loadCheckoutDraft<CheckoutFormData, CheckoutMode>();

    // Load cart data
    fetchCart().finally(() => setCartLoaded(true));

    // Load wallet balance for authenticated users (silently — wallet is optional)
    if (user) {
      walletService.getMyWallet()
        .then(w => setWalletBalance(Number(w.balance)))
        .catch(() => setWalletBalance(null));
    }

    if (savedDraft?.formData) {
      form.setFieldsValue(savedDraft.formData);
    }

    if (savedDraft?.reviewCustomerInfo) {
      setReviewCustomerInfo(savedDraft.reviewCustomerInfo);
    }

    if (savedDraft?.checkoutMode === 'payment' || savedDraft?.checkoutMode === 'wallet') {
      // Sanitize legacy 'quotation' drafts — that mode has been removed.
      setCheckoutMode(savedDraft.checkoutMode);
    }

    if (savedDraft && savedDraft.currentStep > 0) {
      setCurrentStep(savedDraft.currentStep);
    }

    // Load customer info if user is logged in
    if (user) {
      loadCustomerInfo(savedDraft?.formData);
    }

    // Load terms and conditions
    loadTermsAndConditions();

    hasHydratedDraft.current = true;
  }, [user]);

  useEffect(() => {
    if (!hasHydratedDraft.current) {
      return;
    }

    persistCheckoutDraft();
  }, [reviewCustomerInfo, currentStep, checkoutMode]);

  // Each checkout step replaces the page's content without changing the URL,
  // so App's scroll-to-top on route change never fires. On a phone the buttons
  // sit below a long review, and Proceed to Payment left the customer looking
  // at the footer of the payment step (issue #155). Start every step at the top.
  const previousStep = useRef(currentStep);
  useEffect(() => {
    if (previousStep.current === currentStep) {
      return;
    }
    previousStep.current = currentStep;
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Redirect if cart is empty — but only once the cart has actually been
  // fetched. `items` starts empty, so without this gate a hard load or reload of
  // /checkout bounces to /products before the cart arrives, even when it's full.
  useEffect(() => {
    if (cartLoaded && items.length === 0 && currentStep === 0) {
      message.warning('Your cart is empty. Please add items before checkout.');
      navigate('/products');
    }
  }, [cartLoaded, items.length, currentStep, navigate]);

  const loadCustomerInfo = async (draftFormData?: Partial<CheckoutFormData>) => {
    try {
      const response = await QuotationService.getCustomerInfo();
      if (response.success && response.data) {
        setCustomerInfo(response.data);
        const customerFormData: Partial<CheckoutFormData> = {
          traderName: response.data.TraderName,
          address1: response.data.address1,
          address2: response.data.address2,
          address3: response.data.address3,
          tel: response.data.tel,
          email: response.data.Email
        };

        const mergedFormData = {
          ...customerFormData,
          ...(draftFormData ?? {})
        };

        // Pre-fill form with customer data
        form.setFieldsValue(mergedFormData);
        persistCheckoutDraft(mergedFormData);
      }
    } catch (error) {
      console.error('Error loading customer info:', error);
    }
  };

  const loadTermsAndConditions = async () => {
    try {
      const response = await QuotationService.getTermsAndConditions('checkout');
      if (response.success && response.data) {
        const lines = response.data.terms
          ?? [response.data.term1, response.data.term2, response.data.term3];
        setTerms(lines.filter((line): line is string => !!line && line.trim().length > 0));
      }
    } catch (error) {
      console.error('Error loading terms:', error);
    }
  };

  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        // Validate form before proceeding
        const validatedFormData = await form.validateFields();
        setReviewCustomerInfo(validatedFormData);
        setCurrentStep(1);
      } else if (currentStep === 1) {
        if (!agreedToTerms) {
          message.error('Please agree to the Terms and Conditions to continue.');
          return;
        }
        if (checkoutMode === 'wallet') {
          // Wallet path — submit directly, debit happens server-side, no gateway round-trip
          await processCheckout();
        } else {
          // Card path (Razorpay) — advance to payment step
          setCurrentStep(2);
        }
      }
    } catch (error: any) {
      const errorFields = error?.errorFields;
      if (Array.isArray(errorFields) && errorFields.length > 0) {
        message.error('Please complete all required fields before continuing.');
        form.scrollToField(errorFields[0].name, { behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const processCheckout = async (payment?: RazorpayCheckoutSuccess) => {
    // Fall back to the persisted draft before giving up. React state is lost on
    // a page reload, and bailing out here AFTER Razorpay has captured the money
    // leaves the customer charged with no order and no way back.
    const customer =
      reviewCustomerInfo ?? loadCheckoutDraft<CheckoutFormData, CheckoutMode>()?.reviewCustomerInfo ?? null;

    if (!customer) {
      if (payment) {
        // Payment already taken — never let this look like "try again", or they
        // will be charged a second time.
        setOrphanedPayment(payment.razorpay_payment_id);
      } else {
        message.error('Please review and confirm your customer information again.');
        setCurrentStep(0);
      }
      return;
    }

    setLoading(true);

    try {
      // Prepare checkout data
      const checkoutData: CheckoutRequest = {
        customerInfo: {
          traderName: customer.traderName,
          address1: customer.address1,
          address2: customer.address2 || '',
          address3: customer.address3 || '',
          tel: customer.tel,
          email: customer.email
        },
        cartItems: items.map(item => ({
          SupersedeNo: item.SupersedeNo,
          UOM: item.UOM,
          Description: item.Description,
          Qty: item.Qty,
          Price: item.Price,
          ConvRate: item.ConvRate
        })),
        cartSummary: {
          subtotal: checkoutSummary.subtotal,
          deliveryAmount: checkoutSummary.deliveryAmount,
          gstAmount: checkoutSummary.gstAmount,
          grandTotal: checkoutSummary.grandTotal,
          gstFlag: checkoutSummary.gstFlag,
          gstPercent: checkoutSummary.gstPercent,
          discountAmt: checkoutSummary.discountAmt
        },
        priceLevel: user?.priceLevel || 'GN',
        // Razorpay verification triple, present when a card payment was made.
        razorpayOrderId: payment?.razorpay_order_id,
        razorpayPaymentId: payment?.razorpay_payment_id,
        razorpaySignature: payment?.razorpay_signature,
        paymentMethod: checkoutMode === 'wallet' ? 'wallet' : 'card',
        promoCode: appliedPromo?.valid ? appliedPromo.code : undefined
      };

      // Process checkout
      const result = user
        ? await QuotationService.processCheckout(checkoutData)
        : await QuotationService.processGuestCheckout(checkoutData);

      if (result.success && result.quotationRefNo) {
        clearCheckoutDraft();
        setQuotationRefNo(result.quotationRefNo);
        setCurrentStep(checkoutMode === 'payment' ? 3 : 2);
        clearCart(); // Clear the cart after successful checkout
        message.success(`Order ${result.quotationRefNo} placed successfully`);
      } else if (payment) {
        // Paid, but no order. "Try again" would charge them twice.
        setOrphanedPayment(payment.razorpay_payment_id);
      } else {
        message.error(result.message || 'Checkout failed. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      if (payment) {
        setOrphanedPayment(payment.razorpay_payment_id);
      } else {
        message.error('Checkout failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (payment: RazorpayCheckoutSuccess) => {
    setPaymentId(payment.razorpay_payment_id);
    // Process checkout with the Razorpay verification triple
    await processCheckout(payment);
  };

  const handlePaymentError = (error: string) => {
    // PaymentForm already shows the error inline (and Razorpay shows its own on
    // gateway failures) — don't stack a duplicate toast on top of those.
    console.warn('Payment error:', error);
  };

  // Cart summary table columns
  const cartColumns = [
    {
      title: 'Product',
      dataIndex: 'Description',
      key: 'product',
      render: (text: string, record: any) => (
        <Row align="middle" gutter={12}>
          <Col>
            <Image
              width={50}
              height={50}
              src={ProductService.getProductImageUrl(record.MainPictureFull)}
              alt={record.Description}
              fallback="/images/product-placeholder.svg"
              style={{ borderRadius: 4 }}
            />
          </Col>
          <Col flex="1">
            <div>
              <Text strong>{text}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '0.9em' }}>
                {record.SupersedeNo} | {record.UOM}
              </Text>
            </div>
          </Col>
        </Row>
      )
    },
    {
      title: 'Price',
      dataIndex: 'PriceDesc1',
      key: 'price',
      align: 'right' as const,
      width: 120
    },
    {
      title: 'Qty',
      dataIndex: 'Qty',
      key: 'quantity',
      align: 'center' as const,
      width: 80
    },
    {
      title: 'Total',
      key: 'total',
      align: 'right' as const,
      width: 120,
      render: (record: any) => (
        <Text strong>{formatCurrency(toNumber(record.Price) * toNumber(record.Qty))}</Text>
      )
    }
  ];

  // On a phone the four-column table scrolled sideways, so customers swiped
  // between an item's name and its price, qty and total (issue #143). Each
  // item is a card instead, laid out like the cart's mobile list, with all of
  // its numbers together.
  const renderMobileReviewItems = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {items.map((record: any) => (
        <Card key={record.SupersedeNoUOM} size="small" style={{ borderRadius: 8 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Image
              width={56}
              height={56}
              src={ProductService.getProductImageUrl(record.MainPictureFull)}
              alt={record.Description}
              fallback="/images/product-placeholder.svg"
              preview={false}
              style={{ borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong>{record.Description}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '0.85em' }}>
                {record.UOM} • {record.SupersedeNo}
              </Text>
            </div>
          </div>
          <Divider style={{ margin: '10px 0' }} />
          <Row justify="space-between" align="middle" wrap={false}>
            <Text type="secondary">
              {record.PriceDesc1} × {record.Qty}
            </Text>
            <Text strong>{formatCurrency(toNumber(record.Price) * toNumber(record.Qty))}</Text>
          </Row>
        </Card>
      ))}
    </Space>
  );

  // Render customer information form
  const renderCustomerForm = () => (
    <Card title="Customer Information" style={{ marginBottom: 24 }}>
      <Alert
        message="Fields marked with * are required to continue."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Form
        form={form}
        layout="vertical"
        requiredMark
        onValuesChange={(_, allValues) => persistCheckoutDraft(allValues)}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="traderName"
              label="Company/Trader Name"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: 'Please enter your email address' },
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="tel"
              label="Phone Number"
              rules={[{ required: true, message: 'Please enter your phone number' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="address1"
          label="Address Line 1"
          rules={[{ required: true, message: 'Please enter your address' }]}
        >
          <Input />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="address2" label="Address Line 2">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="address3" label="Address Line 3">
              <Input />
            </Form.Item>
          </Col>
        </Row>

      </Form>
    </Card>
  );

  // Render order review with checkout mode selection
  const renderOrderReview = () => (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="Order Review">
        {screens.md ? (
          <Table
            dataSource={items}
            columns={cartColumns}
            pagination={false}
            rowKey="SupersedeNoUOM"
            size="small"
            scroll={{ x: 'max-content' }}
          />
        ) : (
          renderMobileReviewItems()
        )}

        <Divider />

        <Row justify="end">
          <Col xs={24} sm={16} md={12}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {/* Promo entry — only shown when no code is applied yet. Once a code
                  is applied it appears as a single Discount line below, in order. */}
              {!appliedPromo?.valid && (
                <>
                  <Text type="secondary" style={{ fontSize: 13 }}>Have a promo code?</Text>
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      placeholder="Enter code"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value)}
                      onPressEnter={handleApplyPromo}
                      disabled={promoLoading}
                    />
                    <Button type="primary" ghost onClick={handleApplyPromo} loading={promoLoading}>Apply</Button>
                  </Space.Compact>
                  {promoError && <Text type="danger" style={{ fontSize: 12 }}>{promoError}</Text>}
                  <Divider style={{ margin: '8px 0' }} />
                </>
              )}

              <Row justify="space-between">
                <Text type="secondary">Subtotal</Text>
                <Text>{formatCurrency(checkoutSummary.subtotal)}</Text>
              </Row>

              {appliedPromo?.valid && checkoutSummary.discountAmt > 0 && (
                <Row justify="space-between" align="middle">
                  <Space size={6}>
                    <Text type="success">Discount</Text>
                    <Tag color="success" style={{ marginInlineEnd: 0 }}>{appliedPromo.code}</Tag>
                    <Button type="link" size="small" onClick={handleRemovePromo} style={{ padding: 0, height: 'auto' }}>
                      Remove
                    </Button>
                  </Space>
                  <Text type="success">−{formatCurrency(checkoutSummary.discountAmt)}</Text>
                </Row>
              )}

              <Row justify="space-between">
                <Text type="secondary">Delivery</Text>
                <Text>{formatCurrency(checkoutSummary.deliveryAmount)}</Text>
              </Row>
              {checkoutSummary.gstAmount > 0 && (
                <Row justify="space-between">
                  <Text type="secondary">GST ({checkoutSummary.gstPercent}%)</Text>
                  <Text>{formatCurrency(checkoutSummary.gstAmount)}</Text>
                </Row>
              )}

              <Divider style={{ margin: '8px 0' }} />
              <Row justify="space-between" align="middle">
                <Text strong style={{ fontSize: '1.15em' }}>Total</Text>
                <Space direction="vertical" align="end" size={0}>
                  <Text strong style={{ fontSize: '1.25em', color: 'var(--trust-blue)' }}>
                    {formatCurrency(checkoutSummary.grandTotal)}
                  </Text>
                  {appliedPromo?.valid && checkoutSummary.discountAmt > 0 && (
                    <Text type="success" style={{ fontSize: 12 }}>
                      You saved {formatCurrency(checkoutSummary.discountAmt)}
                    </Text>
                  )}
                </Space>
              </Row>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card title="Customer Information">
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Text strong>Company:</Text> {reviewCustomerInfo?.traderName || '-'}<br />
            <Text strong>Email:</Text> {reviewCustomerInfo?.email || '-'}<br />
            <Text strong>Phone:</Text> {reviewCustomerInfo?.tel || '-'}
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Address:</Text><br />
            {reviewCustomerInfo?.address1 || '-'}<br />
            {reviewCustomerInfo?.address2 && <>{reviewCustomerInfo.address2}<br /></>}
            {reviewCustomerInfo?.address3 && <>{reviewCustomerInfo.address3}<br /></>}
          </Col>
        </Row>
      </Card>

      {/* Checkout Mode Selection */}
      <Card title="How would you like to proceed?">
        <Radio.Group
          value={checkoutMode}
          onChange={(e) => setCheckoutMode(e.target.value)}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {paymentAvailable && (
              <Radio value="payment" style={{ width: '100%' }}>
                <Card
                  size="small"
                  style={{
                    marginLeft: 8,
                    border: checkoutMode === 'payment' ? '2px solid #52c41a' : '1px solid #d9d9d9'
                  }}
                >
                  <Space>
                    <CreditCardOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                    <div>
                      <Text strong>Pay by Card / PayNow</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Card, PayNow, and other methods via Razorpay.
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Radio>
            )}

            {user && walletBalance !== null && (() => {
              const sufficient = walletBalance >= checkoutSummary.grandTotal;
              return (
                <Radio value="wallet" style={{ width: '100%' }} disabled={!sufficient}>
                  <Card
                    size="small"
                    style={{
                      marginLeft: 8,
                      border: checkoutMode === 'wallet' ? '2px solid #722ed1' : '1px solid #d9d9d9',
                      opacity: sufficient ? 1 : 0.65
                    }}
                  >
                    <Space>
                      <WalletOutlined style={{ fontSize: 24, color: '#722ed1' }} />
                      <div>
                        <Text strong>Pay with Wallet</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Balance: SGD {walletBalance.toFixed(2)}
                          {!sufficient && (
                            <> — insufficient. <Link to="/wallet">Top up</Link> to enable.</>
                          )}
                        </Text>
                      </div>
                    </Space>
                  </Card>
                </Radio>
              );
            })()}
          </Space>
        </Radio.Group>

        {!paymentAvailable && (
          <Alert
            message="Card payment is currently unavailable"
            description={
              user
                ? "Please use Pay with Wallet, or top up your wallet first."
                : "Please sign in and use Pay with Wallet to complete your order."
            }
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        {paymentAvailable && user && walletBalance === null && (
          <Alert
            type="info"
            showIcon
            message="Loading wallet balance…"
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      <Card>
        <Checkbox
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
        >
          <Text type="danger" style={{ marginRight: 4 }}>*</Text>
          I agree to the{' '}
          <Button type="link" onClick={() => setTermsVisible(true)} style={{ padding: 0 }}>
            Terms and Conditions
          </Button>
        </Checkbox>
      </Card>
    </Space>
  );

  // Render payment step
  const renderPaymentStep = () => (
    <Row gutter={24}>
      <Col xs={24} lg={16}>
        <PaymentForm
          amount={checkoutSummary.grandTotal}
          currency="SGD"
          quotationRefNo={quotationRefNo || `PENDING-${Date.now()}`}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
          prefill={{
            name: reviewCustomerInfo?.traderName || undefined,
            email: reviewCustomerInfo?.email || undefined,
            contact: reviewCustomerInfo?.tel || undefined,
          }}
        />
      </Col>
      <Col xs={24} lg={8}>
        <Card title="Order Summary" style={{ marginBottom: 16 }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Row justify="space-between">
              <Text>Items ({items.length}):</Text>
              <Text>{formatCurrency(checkoutSummary.subtotal)}</Text>
            </Row>
            {checkoutSummary.discountAmt > 0 && (
              <Row justify="space-between">
                <Text type="success">Discount{appliedPromo?.code ? ` (${appliedPromo.code})` : ''}:</Text>
                <Text type="success">−{formatCurrency(checkoutSummary.discountAmt)}</Text>
              </Row>
            )}
            <Row justify="space-between">
              <Text>Delivery:</Text>
              <Text>{formatCurrency(checkoutSummary.deliveryAmount)}</Text>
            </Row>
            {checkoutSummary.gstAmount > 0 && (
              <Row justify="space-between">
                <Text>GST ({checkoutSummary.gstPercent}%):</Text>
                <Text>{formatCurrency(checkoutSummary.gstAmount)}</Text>
              </Row>
            )}
            <Divider style={{ margin: '8px 0' }} />
            <Row justify="space-between">
              <Text strong>Total:</Text>
              <Text strong style={{ color: 'var(--trust-blue)', fontSize: '1.2em' }}>
                {formatCurrency(checkoutSummary.grandTotal)}
              </Text>
            </Row>
          </Space>
        </Card>

        <Alert
          message="Secure Payment"
          description="Your payment is processed securely by Razorpay. We never store your full card details."
          type="success"
          showIcon
        />
      </Col>
    </Row>
  );

  // Render success page
  const renderSuccess = () => {
    const guestEmail = reviewCustomerInfo?.email;
    return (
      <>
        <Result
          status="success"
          title="Order Placed Successfully!"
          subTitle={
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Text>Your order number is: <Text strong>{quotationRefNo}</Text></Text>
              {paymentId ? (
                <Text>Your payment has been processed. A confirmation email is on the way.</Text>
              ) : checkoutMode === 'wallet' ? (
                <Text>Your wallet has been charged for this order. A confirmation email is on the way.</Text>
              ) : (
                <Text>Your order has been placed. A confirmation email is on the way.</Text>
              )}
            </Space>
          }
          extra={[
            <Button type="primary" key="products" onClick={() => navigate('/products')}>
              Continue Shopping
            </Button>,
            <Button key="home" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          ]}
        />

        {/* Guest checkout: invite them to create an account to track this + past orders */}
        {!user && (
          <Card style={{ maxWidth: 520, margin: '0 auto' }}>
            <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
              <Text strong>Want to track this order?</Text>
              <Text type="secondary">
                Create an account and we'll link this order — and any other orders placed with your
                email — so you can view them anytime.
              </Text>
              <Button
                type="primary"
                onClick={() =>
                  navigate(`/register${guestEmail ? `?email=${encodeURIComponent(guestEmail)}` : ''}`)
                }
              >
                Create an account
              </Button>
            </Space>
          </Card>
        )}
      </>
    );
  };

  // Determine steps based on checkout mode
  const getSteps = () => {
    const baseSteps = [
      { title: 'Customer Info', icon: <UserOutlined /> },
      { title: 'Review Order', icon: <ShoppingCartOutlined /> }
    ];

    if (checkoutMode === 'payment') {
      baseSteps.push({ title: 'Payment', icon: <CreditCardOutlined /> });
    }

    baseSteps.push({ title: 'Complete', icon: <CheckCircleOutlined /> });

    return baseSteps;
  };

  // Razorpay took the money but we could not create the order. This takes
  // priority over every other view — including the empty-cart screen, which
  // would otherwise hide the fact that a payment is outstanding.
  if (orphanedPayment) {
    return (
      <div className="checkout-page" style={{ padding: 'var(--spacing-lg)' }}>
        <div className="container">
          <Result
            status="warning"
            title="Your payment went through, but we couldn't create your order"
            subTitle={
              <>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text strong>Please do not pay again.</Text> Your payment has been received and
                  we have a record of it. Quote the reference below and our team will either
                  complete your order or refund you.
                </Paragraph>
                <Paragraph copyable={{ text: orphanedPayment }} style={{ marginBottom: 0 }}>
                  Payment reference: <Text code>{orphanedPayment}</Text>
                </Paragraph>
              </>
            }
            extra={
              <Link to="/products">
                <Button type="primary">Continue Shopping</Button>
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  if (items.length === 0 && currentStep === 0) {
    return (
      <div className="checkout-page" style={{ padding: 'var(--spacing-lg)' }}>
        <div className="container">
          <Result
            icon={<ShoppingCartOutlined />}
            title="Your cart is empty"
            subTitle="Add some products to your cart before checkout"
            extra={
              <Link to="/products">
                <Button type="primary">Continue Shopping</Button>
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const steps = getSteps();
  const isSuccessStep = currentStep === steps.length - 1;
  const isPaymentStep = checkoutMode === 'payment' && currentStep === 2;

  return (
    <div className="checkout-page" style={{ padding: 'var(--spacing-lg)', background: '#f5f5f5' }}>
      <div className="container">
        <Card style={{ marginBottom: 24 }}>
          <Row align="middle" justify="space-between" style={{ marginBottom: 24 }}>
            <Col>
              <Title level={2} style={{ margin: 0, color: 'var(--trust-blue)' }}>
                Checkout
              </Title>
            </Col>
            <Col>
              <Link to="/cart">
                <Button icon={<LeftOutlined />}>Back to Cart</Button>
              </Link>
            </Col>
          </Row>

          <Steps
            current={currentStep}
            direction={screens.md ? 'horizontal' : 'vertical'}
            size={screens.md ? 'default' : 'small'}
            style={{ marginBottom: 32 }}
          >
            {steps.map((step, index) => (
              <Step key={index} title={step.title} icon={step.icon} />
            ))}
          </Steps>

          <div style={{ minHeight: 400 }}>
            {currentStep === 0 && renderCustomerForm()}
            {currentStep === 1 && renderOrderReview()}
            {isPaymentStep && renderPaymentStep()}
            {isSuccessStep && renderSuccess()}
          </div>

          {!isSuccessStep && !isPaymentStep && (
            <Row justify="space-between" align="middle" style={{ marginTop: 24 }}>
              <Col>
                {currentStep > 0 && (
                  <Button onClick={handleBack}>
                    Back
                  </Button>
                )}
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={handleNext}
                  loading={loading}
                  size="large"
                  style={{ minWidth: 120 }}
                >
                  {currentStep === 0
                    ? 'Continue'
                    : checkoutMode === 'payment'
                      ? 'Proceed to Payment'
                      : 'Pay with Wallet'}
                </Button>
              </Col>
            </Row>
          )}

          {isPaymentStep && (
            <Row justify="start" style={{ marginTop: 24 }}>
              <Button onClick={handleBack}>
                Back to Review
              </Button>
            </Row>
          )}
        </Card>

        {/* Terms and Conditions Modal */}
        <Modal
          title="Terms and Conditions"
          open={termsVisible}
          onCancel={() => setTermsVisible(false)}
          footer={[
            <Button key="close" onClick={() => setTermsVisible(false)}>
              Close
            </Button>
          ]}
          width={600}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {terms.map((line, idx) => (
              <Paragraph key={idx}>{line}</Paragraph>
            ))}
          </Space>
        </Modal>
      </div>
    </div>
  );
};

export default Checkout;
